import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { formatDistanceToNow, format } from 'date-fns';
import {
  FlaskConical, Plus, Play, CheckCircle2, XCircle, Clock,
  ArrowRight, BookOpen, RefreshCw, ChevronDown, ChevronUp,
} from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────

interface Experiment {
  id: string;
  org_id: string;
  org_name: string;
  experiment_name: string;
  hypothesis: string;
  status: string;
  experiment_type: string | null;
  effort_level: string | null;
  duration_days: number | null;
  expected_lift: string | null;
  context_note: string | null;
  outcome: string | null;
  result_summary: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  playbook_entry_id: string | null;
}

interface OrgOption { id: string; name: string; }

// ── Constants ─────────────────────────────────────────────────────

const STATUS_META: Record<string, { label: string; color: string; icon: React.ComponentType<{ className?: string }> }> = {
  draft:     { label: 'Draft',    color: 'border-muted text-muted-foreground',                                                  icon: Clock },
  running:   { label: 'Running',  color: 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/30',                  icon: Play },
  complete:  { label: 'Complete', color: 'bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30',              icon: CheckCircle2 },
  abandoned: { label: 'Stopped',  color: 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/30',                      icon: XCircle },
};

const EXPERIMENT_TYPES = [
  { value: 'prompt_drop',   label: 'Targeted Prompt Drop' },
  { value: 'contest',       label: 'Team Contest / Challenge' },
  { value: 'one_on_one',    label: '1:1 First-Win Session' },
  { value: 'drip',          label: 'Daily Drip Sequence' },
  { value: 'nudge',         label: 'Personal Nudge' },
  { value: 'custom',        label: 'Custom' },
];

const EFFORT_LABELS: Record<string, string> = { low: 'Low effort', medium: 'Medium effort', high: 'High (your time)' };

// ── Main component ────────────────────────────────────────────────

export default function ExperimentEngine() {
  const navigate = useNavigate();

  const [experiments, setExperiments] = useState<Experiment[]>([]);
  const [orgs, setOrgs]               = useState<OrgOption[]>([]);
  const [loading, setLoading]         = useState(true);
  const [expanded, setExpanded]       = useState<Set<string>>(new Set());

  // Create dialog
  const [showCreate, setShowCreate] = useState(false);
  const [newOrgId, setNewOrgId]     = useState('');
  const [newName, setNewName]       = useState('');
  const [newHyp, setNewHyp]         = useState('');
  const [newType, setNewType]       = useState('prompt_drop');
  const [newEffort, setNewEffort]   = useState('low');
  const [newDays, setNewDays]       = useState('7');
  const [newLift, setNewLift]       = useState('');
  const [newNote, setNewNote]       = useState('');
  const [saving, setSaving]         = useState(false);

  // Complete / result dialog
  const [completing, setCompleting]   = useState<Experiment | null>(null);
  const [resultText, setResultText]   = useState('');
  const [outcomeVal, setOutcomeVal]   = useState('success');
  const [completeSaving, setCompleteSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [expRes, orgRes] = await Promise.all([
      supabase
        .from('cosmo_experiments')
        .select('id, org_id, experiment_name, hypothesis, status, experiment_type, effort_level, duration_days, expected_lift, context_note, outcome, result_summary, started_at, completed_at, created_at, playbook_entry_id')
        .order('created_at', { ascending: false }),
      supabase.from('organizations').select('id, name').order('name'),
    ]);
    const orgMap = Object.fromEntries((orgRes.data || []).map(o => [o.id, o.name]));
    setExperiments(
      (expRes.data || []).map((e: any) => ({ ...e, org_name: orgMap[e.org_id] ?? '—' }))
    );
    setOrgs((orgRes.data || []).map(o => ({ id: o.id, name: o.name })));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleExpand = (id: string) =>
    setExpanded(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  const createExperiment = async () => {
    if (!newOrgId || !newName.trim() || !newHyp.trim()) {
      toast.error('Org, name, and hypothesis are required');
      return;
    }
    setSaving(true);
    const { error } = await supabase.from('cosmo_experiments').insert({
      org_id: newOrgId,
      experiment_name: newName.trim(),
      hypothesis: newHyp.trim(),
      status: 'draft',
      experiment_type: newType,
      effort_level: newEffort,
      duration_days: newDays ? parseInt(newDays) : null,
      expected_lift: newLift.trim() || null,
      context_note: newNote.trim() || null,
      designed_by: 'Sam',
    });
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success('Experiment created');
    setShowCreate(false);
    setNewOrgId(''); setNewName(''); setNewHyp(''); setNewType('prompt_drop');
    setNewEffort('low'); setNewDays('7'); setNewLift(''); setNewNote('');
    load();
  };

  const startExperiment = async (id: string) => {
    const { error } = await supabase
      .from('cosmo_experiments')
      .update({ status: 'running', started_at: new Date().toISOString() })
      .eq('id', id);
    if (error) { toast.error(error.message); return; }
    toast.success('Experiment started');
    load();
  };

  const completeExperiment = async () => {
    if (!completing) return;
    setCompleteSaving(true);
    const { error } = await supabase
      .from('cosmo_experiments')
      .update({
        status: 'complete',
        outcome: outcomeVal,
        result_summary: resultText.trim(),
        completed_at: new Date().toISOString(),
      })
      .eq('id', completing.id);
    setCompleteSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success('Experiment completed');
    setCompleting(null);
    setResultText(''); setOutcomeVal('success');
    load();
  };

  const addToPlaybook = (exp: Experiment) =>
    navigate(`/admin/growth/playbook?from_experiment=${exp.id}&org=${exp.org_id}`);

  // Group experiments
  const running   = experiments.filter(e => e.status === 'running');
  const drafts    = experiments.filter(e => e.status === 'draft');
  const completed = experiments.filter(e => e.status === 'complete' || e.status === 'abandoned');

  const ExperimentCard = ({ exp }: { exp: Experiment }) => {
    const meta = STATUS_META[exp.status] ?? STATUS_META.draft;
    const Icon = meta.icon;
    const open = expanded.has(exp.id);
    const daysRunning = exp.started_at
      ? Math.floor((Date.now() - new Date(exp.started_at).getTime()) / 86400000)
      : null;

    return (
      <Card className="overflow-hidden">
        <div
          className="flex items-start justify-between p-4 cursor-pointer hover:bg-muted/20"
          onClick={() => toggleExpand(exp.id)}
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className={`text-xs ${meta.color}`}>
                <Icon className="h-3 w-3 mr-1" />
                {meta.label}
              </Badge>
              <span className="text-xs text-muted-foreground">{exp.org_name}</span>
              {exp.experiment_type && (
                <span className="text-xs text-muted-foreground">
                  · {EXPERIMENT_TYPES.find(t => t.value === exp.experiment_type)?.label ?? exp.experiment_type}
                </span>
              )}
              {exp.status === 'running' && daysRunning !== null && exp.duration_days && (
                <span className="text-xs text-muted-foreground ml-auto">
                  Day {daysRunning + 1} of {exp.duration_days}
                </span>
              )}
            </div>
            <p className="font-medium text-sm">{exp.experiment_name}</p>
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{exp.hypothesis}</p>
          </div>
          <div className="shrink-0 ml-3 mt-0.5">
            {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
          </div>
        </div>

        {open && (
          <div className="border-t border-border/50 px-4 pb-4 pt-3 space-y-3">
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Hypothesis</p>
              <p className="text-sm">{exp.hypothesis}</p>
            </div>
            {exp.expected_lift && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Expected lift</p>
                <p className="text-sm">{exp.expected_lift}</p>
              </div>
            )}
            {exp.context_note && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Context</p>
                <p className="text-sm text-muted-foreground">{exp.context_note}</p>
              </div>
            )}
            {exp.result_summary && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Result</p>
                <p className="text-sm">{exp.result_summary}</p>
              </div>
            )}
            <div className="flex flex-wrap gap-2 pt-1">
              {exp.status === 'draft' && (
                <Button size="sm" onClick={() => startExperiment(exp.id)}>
                  <Play className="h-3.5 w-3.5 mr-1.5" />Launch
                </Button>
              )}
              {exp.status === 'running' && (
                <Button size="sm" onClick={() => { setCompleting(exp); setResultText(exp.result_summary ?? ''); }}>
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />Complete
                </Button>
              )}
              {exp.status === 'complete' && !exp.playbook_entry_id && (
                <Button size="sm" variant="outline" onClick={() => addToPlaybook(exp)}>
                  <BookOpen className="h-3.5 w-3.5 mr-1.5" />Add to Playbook
                </Button>
              )}
              {exp.status === 'complete' && exp.playbook_entry_id && (
                <Button size="sm" variant="outline" onClick={() => navigate('/admin/growth/playbook')}>
                  <BookOpen className="h-3.5 w-3.5 mr-1.5" />View Playbook Entry
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Created {formatDistanceToNow(new Date(exp.created_at), { addSuffix: true })}
              {exp.effort_level && ` · ${EFFORT_LABELS[exp.effort_level]}`}
              {exp.duration_days && ` · ${exp.duration_days} days`}
            </p>
          </div>
        )}
      </Card>
    );
  };

  const SectionHeader = ({ title, count, children }: { title: string; count: number; children?: React.ReactNode }) => (
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{title}</h2>
        <Badge variant="outline" className="text-xs">{count}</Badge>
      </div>
      {children}
    </div>
  );

  return (
    <div className="p-6 space-y-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Experiment Engine</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Design, run, and learn from engagement experiments across all orgs</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button size="sm" onClick={() => setShowCreate(true)}>
            <Plus className="h-3.5 w-3.5 mr-1.5" />New Experiment
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <FlaskConical className="h-5 w-5 animate-pulse mr-2" />
          <span className="text-sm">Loading experiments…</span>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Running */}
          {running.length > 0 && (
            <div>
              <SectionHeader title="Running" count={running.length} />
              <div className="space-y-2">{running.map(e => <ExperimentCard key={e.id} exp={e} />)}</div>
            </div>
          )}

          {/* Drafts */}
          {drafts.length > 0 && (
            <div>
              <SectionHeader title="Drafts" count={drafts.length} />
              <div className="space-y-2">{drafts.map(e => <ExperimentCard key={e.id} exp={e} />)}</div>
            </div>
          )}

          {/* Completed */}
          {completed.length > 0 && (
            <div>
              <SectionHeader title="Completed" count={completed.length} />
              <div className="space-y-2">{completed.map(e => <ExperimentCard key={e.id} exp={e} />)}</div>
            </div>
          )}

          {experiments.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <FlaskConical className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
                <p className="font-medium mb-1">No experiments yet</p>
                <p className="text-sm text-muted-foreground mb-4">Design your first engagement experiment to start building the playbook.</p>
                <Button onClick={() => setShowCreate(true)}>
                  <Plus className="h-4 w-4 mr-1.5" />New Experiment
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>New Experiment</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Client org *</Label>
              <Select value={newOrgId} onValueChange={setNewOrgId}>
                <SelectTrigger><SelectValue placeholder="Select org…" /></SelectTrigger>
                <SelectContent>{orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Experiment name *</Label>
              <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. Sales Prompt Drop — Multiplier" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Hypothesis *</Label>
              <Textarea value={newHyp} onChange={e => setNewHyp(e.target.value)} rows={3} placeholder="If we send a role-specific prompt to L0 Sales users, they will try it within 48 hours at a higher rate than generic onboarding." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Experiment type</Label>
                <Select value={newType} onValueChange={setNewType}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{EXPERIMENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Effort</Label>
                <Select value={newEffort} onValueChange={setNewEffort}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Duration (days)</Label>
                <Input type="number" value={newDays} onChange={e => setNewDays(e.target.value)} min={1} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Expected lift</Label>
                <Input value={newLift} onChange={e => setNewLift(e.target.value)} placeholder="e.g. 60% try it within 48h" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Context note</Label>
              <Textarea value={newNote} onChange={e => setNewNote(e.target.value)} rows={2} placeholder="Why now? Any relevant org context…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={createExperiment} disabled={saving}>{saving ? 'Saving…' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete Dialog */}
      <Dialog open={!!completing} onOpenChange={() => setCompleting(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Complete Experiment</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{completing?.experiment_name}</p>
            <div className="space-y-1.5">
              <Label className="text-xs">Outcome</Label>
              <Select value={outcomeVal} onValueChange={setOutcomeVal}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="success">Success — beat expectations</SelectItem>
                  <SelectItem value="partial">Partial — some movement</SelectItem>
                  <SelectItem value="failure">Failed — no meaningful lift</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Result summary</Label>
              <Textarea value={resultText} onChange={e => setResultText(e.target.value)} rows={4} placeholder="What happened? Include specific numbers — who tried it, what rate, any standout moments." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleting(null)}>Cancel</Button>
            <Button onClick={completeExperiment} disabled={completeSaving}>{completeSaving ? 'Saving…' : 'Complete'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
